/**
 * NarrationPlayer — Phase 7 Plan 07-02 (AUDIO-03 + FALL-03 v1).
 *
 * Headless <audio> + <track kind="captions"> bound to useNarrationStore. Zero
 * visible chrome: the player itself is display:none, and sighted caption UI is
 * owned by the existing Phase 5 DOM CaptionOverlay (aria-live=polite, period
 * serif chrome). The native <track> feeds browser caption menus so
 * screen-reader users and caption-menu users get FALL-03 at the accessibility
 * gold standard — dual-surface captions per 07-RESEARCH.md Pattern 3.
 *
 * State flow:
 *   1. User starts a tour in TourMenu → useTourStore.setActiveTour('grand-tour-1925')
 *   2. This component's useEffect mirrors useTourStore.activeTourId into
 *      useNarrationStore.setActiveTour(...).
 *   3. When useNarrationStore.activeTourId changes, a second useEffect calls
 *      audio.play() or audio.pause() based on the muted flag.
 *   4. Plan 07-06's MuteButton toggles useNarrationStore.muted; this effect
 *      re-runs and pauses/plays accordingly.
 *
 * Audio autoplay-policy: HTMLAudioElement.play() from a user-initiated tour
 * click is itself a gesture-triggered play, so we don't need to piggyback on
 * BoardingOverlay's AudioContext unlock (see Phase 5 Plan 05-05 for the
 * ambient-audio pattern). If play() is blocked for any other reason we
 * silently fail — the user can toggle mute/unmute to retry, which is a
 * gesture event.
 *
 * Author-time ONLY assets: the src URLs resolve to static .mp3 + .vtt files
 * in /public. Zero runtime coupling to ElevenLabs (see 07-RESEARCH.md
 * Pitfall 9).
 */
import { useEffect, useRef } from 'react';
import { useTourStore } from '../store/useTourStore';
import { useNarrationStore } from './useNarrationStore';

export function NarrationPlayer() {
  const tourStoreActive = useTourStore((s) => s.activeTourId);
  const activeTourId = useNarrationStore((s) => s.activeTourId);
  const muted = useNarrationStore((s) => s.muted);
  const setActiveTour = useNarrationStore((s) => s.setActiveTour);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Mirror useTourStore.activeTourId into useNarrationStore so TourMenu's
  // existing contract with useTourStore also drives narration playback.
  useEffect(() => {
    setActiveTour(tourStoreActive);
  }, [tourStoreActive, setActiveTour]);

  // Drive <audio> playback from the narration store.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (!activeTourId) {
      a.pause();
      return;
    }
    if (muted) {
      a.pause();
    } else {
      a.play().catch(() => {
        // Autoplay blocked or load race — silent fail is correct. User can
        // retry via MuteButton toggle (Plan 07-06) which counts as a gesture.
      });
    }
  }, [activeTourId, muted]);

  if (!activeTourId) return null;

  return (
    <audio
      ref={audioRef}
      preload="auto"
      style={{ display: 'none' }}
      aria-label="Guided tour narration"
    >
      <source src={`/audio/tours/${activeTourId}.mp3`} type="audio/mpeg" />
      <track
        kind="captions"
        src={`/captions/${activeTourId}.vtt`}
        srcLang="en"
        label="English"
        default
      />
    </audio>
  );
}
